import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { Facebook, Instagram, Linkedin, Twitter } from 'react-bootstrap-icons';
import DropdownButton from './Dropdownbutton';
import CardComponent from './CardComponent';
import SEOHead from '../components/SEOHead';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import whiteLogo from '../assets/whitebglogo.png';
import androidQrCode from '../assets/playstoreqr.png';
import iosQrCode from '../assets/iosqrcode.png';
import '../App.css';

const ClassResources = () => {
  const { grade } = useParams();
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState('');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryNames, setCategoryNames] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const validGrades = ['9', '10', '11', '12'];
  const isValidGrade = validGrades.includes(grade);

  useEffect(() => {
    if (!isValidGrade) {
      navigate('/');
      return;
    }

    const fetchSubjects = async () => {
      try {
        const ref = doc(db, 'subjects', grade);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const subjects = snap.data();
          setCategoryNames(subjects);
          const firstCategory = Object.keys(subjects)[0];
          setSelectedCategory(firstCategory);
        }
      } catch (err) {
        console.error('Error fetching subjects:', err);
      }
    };

    fetchSubjects();
  }, [grade, navigate, isValidGrade]);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedCategory) return;
      setLoading(true);
      try {
        const q = query(
          collection(db, 'resources'),
          where('grade', '==', grade),
          where('subject', '==', selectedCategory)
        );
        const snapshot = await getDocs(q);
        const results = [];
        snapshot.forEach((doc) => results.push({ ...doc.data(), id: doc.id }));
        setCards(results);
      } catch (err) {
        console.error('Error fetching resources:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [grade, selectedCategory]);

  const handleShowOffcanvas = (card) => {
    // Navigate to dedicated page using slug from chapterTitle
    const slug = (card.chapterTitle || '').toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    navigate(`/class/${grade}/chapter/${slug}`);
  };

  // Generate SEO description
  const seoDescription = `Access comprehensive CBSE Class ${grade} study materials, notes, video lectures, and practice questions. Free curated resources from top educational platforms for all subjects including ${Object.keys(categoryNames).join(', ')}.`;

  // Generate structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `CBSE Class ${grade} Resources`,
    "description": seoDescription,
    "provider": {
      "@type": "Organization",
      "name": "Cubegon - Numeric Education Pvt. Ltd.",
      "url": "https://cbseresources.com"
    },
    "educationalLevel": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": `Class ${grade}`
    },
    "inLanguage": "en-IN",
    "isAccessibleForFree": true,
    "url": `https://cbseresources.com/class/${grade}`
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://cbseresources.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": `Class ${grade}`,
        "item": `https://cbseresources.com/class/${grade}`
      }
    ]
  };

  return (
    <div className="modern-class-page">
      <SEOHead
        title={`CBSE Class ${grade} Resources - Notes, Lectures & Study Materials`}
        description={seoDescription}
        keywords={`CBSE Class ${grade}, CBSE notes, CBSE study materials, CBSE video lectures, CBSE practice questions, free CBSE resources, Class ${grade} ${Object.keys(categoryNames).join(', ')}`}
        canonical={`/class/${grade}`}
        structuredData={structuredData}
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbStructuredData)}
      </script>
      <Container fluid className="px-4 px-lg-5">
        {/* Header Section */}
        <section className="class-header-section">
          <Container>
                <div className="class-header-card">
                <div className="class-header-main">
                  <div className="class-header-text">
                    <h1 className="class-title">Class {grade} Resources</h1>
                    <p className="class-subtitle">
                      {grade === '9' && "Master the fundamentals of CBSE Class 9 with comprehensive study materials, detailed notes, practice questions, and video lectures covering Mathematics, Science, Social Studies, English, and Hindi. Build a strong foundation for your academic journey."}
                      {grade === '10' && "Excel in CBSE Class 10 board exams with our complete study package including NCERT solutions, sample papers, revision notes, and expert-curated content for all subjects. Prepare confidently for your first major academic milestone."}
                      {grade === '11' && "Transition smoothly to CBSE Class 11 with specialized study materials for Science, Commerce, and Arts streams. Access detailed notes, solved examples, and practice materials to excel in your chosen field of study."}
                      {grade === '12' && "Ace your CBSE Class 12 board exams with our comprehensive study resources including NCERT solutions, previous year papers, mock tests, and expert guidance. Achieve your academic goals with confidence."}
                    </p>
                    {/* Breadcrumb Navigation */}
                    <nav aria-label="breadcrumb" style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                      <ol className="breadcrumb" style={{ marginBottom: 0, backgroundColor: 'transparent', padding: 0 }}>
                        <li className="breadcrumb-item">
                          <Link to="/" style={{ color: 'inherit', textDecoration: 'none', opacity: 0.8 }}>Home</Link>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page" style={{ color: 'inherit' }}>
                          Class {grade}
                        </li>
                      </ol>
                    </nav>
                  </div>
                  
                  <div className="class-header-icon">
                    <div className="class-icon-simple">
                      <span className="class-icon-text">{grade}</span>
                    </div>
                  </div>
                </div>
                
                <div className="class-subject-selection">
                  <div className="subject-dropdown-container">
                    {categoryNames && selectedCategory && (
                      <DropdownButton
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                        buttonColors={{}}
                        categoryNames={categoryNames}
                      />
                    )}
                  </div>
                </div>
              </div>
          </Container>
        </section>


        {/* Resources Section */}
        <section className="resources-section">
          <Container>
            <div className="resources-card">
              {/* Search Bar */}
              <div className="d-flex justify-content-center mb-4">
                <Form.Control
                  type="text"
                  placeholder="Search chapters..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ maxWidth: '520px' }}
                  className="text-center"
                />
              </div>
            

              {/* Loading State */}
              {loading ? (
                <Row className="g-4">
                  {[...Array(6)].map((_, i) => (
                    <Col key={i} md={6} lg={4}>
                      <div className="resource-card-skeleton">
                        <div className="skeleton-header"></div>
                        <div className="skeleton-content">
                          <div className="skeleton-line"></div>
                          <div className="skeleton-line short"></div>
                        </div>
                        <div className="skeleton-button"></div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : cards.length > 0 ? (
                <>
                  <div className="mb-3" style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                    Found {cards.filter((c) => (c.chapterTitle || '').toLowerCase().includes(searchQuery.toLowerCase())).length} chapter{cards.filter((c) => (c.chapterTitle || '').toLowerCase().includes(searchQuery.toLowerCase())).length !== 1 ? 's' : ''} for {selectedCategory && categoryNames[selectedCategory]}
                  </div>
                  <Row className="g-4">
                    {cards
                      .filter((c) => (c.chapterTitle || '').toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((card) => (
                      <Col key={card.id} md={6} lg={4}>
                        <CardComponent
                          card={card}
                          buttonColor="#2563eb"
                          handleShowOffcanvas={handleShowOffcanvas}
                        />
                      </Col>
                    ))}
                  </Row>
                  {/* Internal linking section */}
                  <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--border-color)' }}>
                    <h3 className="h5 mb-3">Explore All Subjects</h3>
                    <div className="d-flex flex-wrap gap-2">
                      {Object.keys(categoryNames).map((cat) => (
                        <Button
                          key={cat}
                          variant={selectedCategory === cat ? 'primary' : 'outline-primary'}
                          size="sm"
                          onClick={() => setSelectedCategory(cat)}
                        >
                          {categoryNames[cat]}
                        </Button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <span className="empty-icon">📚</span>
                  </div>
                  <h4 className="empty-state-title">No Resources Available</h4>
                  <p className="empty-state-text">
                    No resources found for Class {grade} - {selectedCategory && categoryNames[selectedCategory]}
                  </p>
                  <Button
                    variant="outline-primary"
                    onClick={() => navigate('/')}
                    className="empty-state-btn"
                  >
                    Back to Home
                  </Button>
                </div>
              )}
            </div>
          </Container>
        </section>

        {/* Offcanvas disabled when using page navigation */}
      </Container>

      {/* Footer */}
      <footer className="cubegon-footer">
        <div className="cubegon-footer-content">
          <div className="cubegon-footer-main">
            <div className="cubegon-footer-logo">
              <img src={whiteLogo} alt="Cubegon" />
            </div>

            <div className="cubegon-footer-download">
              <h3>Download the App</h3>
              <div className="cubegon-footer-qr-container">
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.cubegon.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={androidQrCode} alt="Android QR code - Download from Play Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>Android</p>
                  </a>
                </div>
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://apps.apple.com/in/app/cubegon-smart-learning/id6752596017" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={iosQrCode} alt="iOS QR code - Download from App Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>iOS</p>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="cubegon-footer-bottom">
            <div className="cubegon-footer-social">
              <a aria-label="Facebook" href="https://facebook.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Facebook />
              </a>
              <a aria-label="Twitter" href="https://twitter.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Twitter />
              </a>
              <a aria-label="Instagram" href="https://instagram.com/cubegon.app" target="_blank" rel="noopener noreferrer">
                <Instagram />
              </a>
              <a aria-label="LinkedIn" href="https://linkedin.com/company/cubegon" target="_blank" rel="noopener noreferrer">
                <Linkedin />
              </a>
            </div>

            <p className="cubegon-footer-text">Made in 🇮🇳 with ❤️</p>
            <p className="cubegon-footer-copyright">© {new Date().getFullYear()} Numeric Education Pvt. Ltd. All rights reserved.</p>
            <div className="cubegon-footer-links">
              <a href="https://cubegon.app/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href="https://cubegon.app/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClassResources;
